import { ChangeEvent, useRef, useState } from "react";
import { toast } from "sonner";

import { m } from "@recobra/i18n/messages";

import { compressImageFile } from "../lib";

const ALLOWED_PROFILE_PHOTO_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_RAW_PROFILE_PHOTO_BYTES = 20 * 1024 * 1024;
const MAX_UPLOADED_PROFILE_PHOTO_BYTES = 2 * 1024 * 1024;

export const useProfilePhotoUpload = () => {
  const [photo, setPhoto] = useState<string | null>();
  const [readingPhoto, setReadingPhoto] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const onPhotoInputChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (
      file.size > MAX_RAW_PROFILE_PHOTO_BYTES ||
      !ALLOWED_PROFILE_PHOTO_TYPES.includes(file.type)
    ) {
      toast.error(m.profile_photo_invalid());
      return;
    }

    setReadingPhoto(true);
    try {
      const compressed = await compressImageFile(file, {
        maxWidthOrHeight: 512,
        mimeType: "image/webp",
      });
      if (compressed.size > MAX_UPLOADED_PROFILE_PHOTO_BYTES) {
        toast.error(m.profile_photo_invalid());
        return;
      }

      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(compressed);
      });
      setPhoto(dataUrl);
    } catch {
      toast.error(m.profile_photo_invalid());
    } finally {
      setReadingPhoto(false);
    }
  };

  return { photo, setPhoto, readingPhoto, photoInputRef, onPhotoInputChange };
};
